import { myHeadersWithTokenOversight } from '../global';
import {
  CreateDelegatedSessionData,
  CreateDelegatedSessionResponse,
  DelegatedSessionActionResponse,
  GetDelegatedSessions,
  GetOversightClient,
  GetOversightClientCompany,
  GetOversightClientOverview,
  GetOversightUser,
  GetOversightUserById,
  GetSessionEvents,
  GetSessionsByClientId,
} from './types';

const OVERSIGHT_BASE_URL =
  process.env.REACT_APP_OVERSIGHT_BASE_URL ||
  'https://oversight.protoai.com.br/api/v1';

export const getOversightClients = (
  params?: string,
  page = 1,
  perPage = 10
): Promise<GetOversightClientOverview> => {
  const reqOptions = {
    method: 'GET',
    headers: myHeadersWithTokenOversight(),
  };

  const query = new URLSearchParams(params ?? '');
  query.set('page', String(page));
  query.set('per_page', String(perPage));

  return fetch(`${OVERSIGHT_BASE_URL}/clients?${query.toString()}`, reqOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getOversightClientsById = (
  id: number
): Promise<GetOversightClient> => {
  const reqOptions = {
    method: 'GET',
    headers: myHeadersWithTokenOversight(),
  };

  return fetch(`${OVERSIGHT_BASE_URL}/clients/${id}`, reqOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getOversightClientsCompany = (
  id: number
): Promise<GetOversightClientCompany> => {
  const reqOptions = {
    method: 'GET',
    headers: myHeadersWithTokenOversight(),
  };

  return fetch(`${OVERSIGHT_BASE_URL}/clients/${id}/companies`, reqOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getOversightUsers = (
  search?: string,
  page = 1,
  perPage = 10
): Promise<GetOversightUser> => {
  const reqOptions = {
    method: 'GET',
    headers: myHeadersWithTokenOversight(),
  };

  const query = new URLSearchParams();
  if (search) query.set('search', search);
  query.set('page', String(page));
  query.set('per_page', String(perPage));

  return fetch(`${OVERSIGHT_BASE_URL}/users?${query.toString()}`, reqOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getOversightUsersById = (
  id: number
): Promise<GetOversightUserById> => {
  const reqOptions = {
    method: 'GET',
    headers: myHeadersWithTokenOversight(),
  };

  return fetch(`${OVERSIGHT_BASE_URL}/users/${id}`, reqOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getDelegatedSessions = (
  page = 1,
  perPage = 10
): Promise<GetDelegatedSessions> => {
  const reqOptions = {
    method: 'GET',
    headers: myHeadersWithTokenOversight(),
  };

  const query = new URLSearchParams();
  query.set('page', String(page));
  query.set('per_page', String(perPage));

  return fetch(
    `${OVERSIGHT_BASE_URL}/delegated-sessions?${query.toString()}`,
    reqOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getDelegatedSessionsByClient = (
  clientId: number,
  params?: string,
  page = 1,
  perPage = 10
): Promise<GetDelegatedSessions> => {
  const reqOptions = {
    method: 'GET',
    headers: myHeadersWithTokenOversight(),
  };

  const query = new URLSearchParams();
  if (params) query.set('search', params);
  if (page) query.set('page', String(page));
  if (perPage) query.set('per_page', String(perPage));

  return fetch(
    `${OVERSIGHT_BASE_URL}/delegated-sessions?client_id=${clientId}&${query.toString()}`,
    reqOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getDelegatedSessionsById = (
  id: number
): Promise<DelegatedSessionActionResponse> => {
  const reqOptions = {
    method: 'GET',
    headers: myHeadersWithTokenOversight(),
  };

  return fetch(`${OVERSIGHT_BASE_URL}/delegated-sessions/${id}`, reqOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getSessionsByClient = (
  clientId: number,
  params?: string,
  page = 1,
  perPage = 10
): Promise<GetSessionsByClientId> => {
  const reqOptions = {
    method: 'GET',
    headers: myHeadersWithTokenOversight(),
  };

  const query = new URLSearchParams();
  if (params) query.set('search', params);
  query.set('page', String(page));
  query.set('per_page', String(perPage));

  return fetch(
    `${OVERSIGHT_BASE_URL}/sessions?client_id=${clientId}&${query.toString()}`,
    reqOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const getSessionEvents = (
  sessionId: number,
  page = 1,
  perPage = 20
): Promise<GetSessionEvents> => {
  const reqOptions = {
    method: 'GET',
    headers: myHeadersWithTokenOversight(),
  };

  const query = new URLSearchParams();
  query.set('session_id', String(sessionId));
  query.set('page', String(page));
  query.set('per_page', String(perPage));
  query.set('order_by', '-occurred_at');

  return fetch(
    `${OVERSIGHT_BASE_URL}/session_events?${query.toString()}`,
    reqOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const createDelegatedSession = (
  data: CreateDelegatedSessionData
): Promise<CreateDelegatedSessionResponse> => {
  const headers = myHeadersWithTokenOversight();
  headers.append('Content-Type', 'application/json');

  const reqOptions = {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  };

  return fetch(`${OVERSIGHT_BASE_URL}/delegated-sessions`, reqOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const cancelDelegatedSession = (
  id: number
): Promise<DelegatedSessionActionResponse> => {
  const headers = myHeadersWithTokenOversight();
  headers.append('Content-Type', 'application/json');

  const reqOptions = {
    method: 'PATCH',
    headers,
  };

  return fetch(
    `${OVERSIGHT_BASE_URL}/delegated-sessions/${id}/cancel`,
    reqOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const startDelegatedSession = (
  id: number
): Promise<DelegatedSessionActionResponse> => {
  const headers = myHeadersWithTokenOversight();
  headers.append('Content-Type', 'application/json');

  const reqOptions = {
    method: 'POST',
    headers,
  };

  return fetch(
    `${OVERSIGHT_BASE_URL}/delegated-sessions/${id}/start`,
    reqOptions
  )
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};

export const endDelegatedSession = (
  id: number
): Promise<DelegatedSessionActionResponse> => {
  const headers = myHeadersWithTokenOversight();
  headers.append('Content-Type', 'application/json');

  const reqOptions = {
    method: 'POST',
    headers,
    body: JSON.stringify({ reason: 'ok' }),
  };

  return fetch(`${OVERSIGHT_BASE_URL}/delegated-sessions/${id}/end`, reqOptions)
    .then((response) => response.text())
    .then((result) => JSON.parse(result))
    .catch((error) => new Error(error));
};
